import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { ElectronicDocumentTypeEnum } from 'src/app/enum/electronic-document-type-enum';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { FilterGlobalResponseModel } from 'src/app/models/responses/filter-global-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InvoiceConceptTableModel } from 'src/app/models/tables/invoicing/invoice-concept-table.model';
import { InvoiceDetailTableModel } from 'src/app/models/tables/invoicing/invoice-detail-table.model';
import { InvoiceTableModel } from 'src/app/models/tables/invoicing/invoice-table.model';
import { HeaderService } from 'src/app/services/header/header.service';
import { InvoiceConceptService } from 'src/app/services/invoicing/invoice-concept.service';
import { InvoiceDetailService } from 'src/app/services/invoicing/invoice-detail.service';
import { InvoiceService } from 'src/app/services/invoicing/invoice.service';
import { ElectronicDocumentInfoComponent } from '../../electronic-document-info/electronic-document-info.component';

@Component({
  selector: 'app-invoice',
  templateUrl: './invoice.component.html',
  styleUrls: ['./invoice.component.scss'],
  standalone: false
})
export class InvoiceComponent implements OnInit {

  @ViewChild('electronicDocumentInfo') electronicDocumentInfoComponent?: CustomModalComponent | null = null;

  private readonly headerService = inject(HeaderService);
  private readonly invoiceConceptService = inject(InvoiceConceptService);
  private readonly invoiceDetailService = inject(InvoiceDetailService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  conceptData: PagedResponseModel<InvoiceConceptTableModel> = new PagedResponseModel<InvoiceConceptTableModel>();
  conceptHeader: FilterGlobalResponseModel = new FilterGlobalResponseModel();
  detailData: PagedResponseModel<InvoiceDetailTableModel> = new PagedResponseModel<InvoiceDetailTableModel>();
  detailHeader: FilterGlobalResponseModel = new FilterGlobalResponseModel();
  filters: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<InvoiceTableModel> = new PagedResponseModel<InvoiceTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  inputsForElectronicDocumentInfo$ = new BehaviorSubject<any>({
    typeDocument: 0,
    documentId: 0
  });
  limit: number = 10;
  listStoreConsolidated: ColumnResponseModel = new ColumnResponseModel();
  nameWindow: string = 'Facturas';
  page: number = 1;
  previousSort: { field: string; isAscsendant: number };
  projectName = ProjectName.projectName;
  storeConsolidated: boolean = true;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
    {
      label: 'Facturación Electrónica',
      icon: 'pi pi-receipt',
      action: 'info'
    }
  ];
  componentElectronicDocumentInfo = ElectronicDocumentInfoComponent;

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} -  ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem('invoice');

    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getInvoicesWithPagination(),
    ]);
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.columns.length = this.detailHeader.columns.length === 1 ? 0 : this.detailHeader.columns.length;
    this.conceptHeader.columns.length = this.conceptHeader.columns.length === 1 ? 0 : this.conceptHeader.columns.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getInvoicesWithPagination()
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getAllInvoiceDetailsWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 2:
        await Promise.all([
          this.getColumnsForConceptConsultation(),
          this.getAllInvoiceConceptsWithPagination()
        ]);
        this.conceptData = {
          ...this.conceptData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getInvoicesWithPagination()
        break;
      case 1:
        await this.getAllInvoiceDetailsWithPagination()
        break;
      case 2:
        await this.getAllInvoiceConceptsWithPagination()
        break;
    }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailConsultation();
        break;
      case 2:
        await this.exportConceptConsultation();
        break;
    }
  }

  async exportGeneralConsultation() {
    try {
      const blob = await firstValueFrom(this.invoiceService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Facturas.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }

    } catch (error) {
      console.error('Error al exportar las facturas.', error);
    }
  }

  async exportDetailConsultation() {
    try {
      const blob = await firstValueFrom(this.invoiceDetailService.exportDetailConsultation(this.filters, this.columnSort, this.ascendant, this.storeConsolidated));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Facturas Detalle.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar las facturas detalle.', error);
    }
  }

  async exportConceptConsultation() {
    try {
      const blob = await firstValueFrom(this.invoiceConceptService.exportConceptConsultation(this.filters, this.columnSort, this.ascendant, this.storeConsolidated));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Factura Concepto.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }

    } catch (error) {
      console.error('Error al exportar las facturas concepto.', error);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.invoiceService.getColumnsForGeneralConsultation());
      }
      this.listStoreConsolidated = new ColumnResponseModel;
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getInvoicesWithPagination() {
    try {

      this.generalData = await firstValueFrom(this.invoiceService.getInvoicesWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters));

    } catch (error) {
      console.error('Error al buscar las facturas.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.columns.length === 0) {
        this.detailHeader = await firstValueFrom(this.invoiceDetailService.getColumnsForDetailConsultation());
      }
      this.listStoreConsolidated = this.detailHeader.filtersGlobal[0];
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getAllInvoiceDetailsWithPagination() {
    try {
      this.detailData = await firstValueFrom(this.invoiceDetailService.getAllInvoiceDetailsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.storeConsolidated, this.filters));
    } catch (error) {
      console.error('Error al buscar los detalles de las facturas.', error);
    }
  }

  async getColumnsForConceptConsultation() {
    try {
      if (this.conceptHeader.columns.length === 0) {
        this.conceptHeader = await firstValueFrom(this.invoiceConceptService.getColumnsForConceptConsultation());
      }
      this.listStoreConsolidated = this.conceptHeader.filtersGlobal[0];
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getAllInvoiceConceptsWithPagination() {
    try {
      this.conceptData = await firstValueFrom(this.invoiceConceptService.getAllInvoiceConceptsWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.storeConsolidated, this.filters));

    } catch (error) {
      console.error('Error al buscar los conceptos de las facturas.', error);
    }
  }

  newInvoice() {
    this.router.navigate(['/invoice/register']);
  }

  onAction(event: { action: string, item?: InvoiceTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/invoice/register/${event.item.invoiceId}`]);
    }
    if (event.action === 'info' && event.item) {
      this.openElectronicDocumentInfo(event.item);
    }
  }

  onStoreConsolidatedChange(value: boolean) {
    this.storeConsolidated = value;
  }

  async openElectronicDocumentInfo(item: InvoiceTableModel) {
    this.inputsForElectronicDocumentInfo$.next({
      typeDocument: ElectronicDocumentTypeEnum.invoice,
      documentId: item.id
    });
    this.electronicDocumentInfoComponent?.show();
  }

}
